import { getCollectors } from "@/api/collector";
import { useCallback, useEffect, useState } from "react";

interface CollectionOrder {
  id: string;
  Customer: Client; // Adjust types as needed
  location: string;
  date: string;
  amount: number;
  status: string;
}

interface useCollectorScrollProps {
  searchTerm?: string;
  perPage?: number;
}

interface CollectionsData {
  collections: CollectionOrder[];
  loading: boolean;
  handleRefresh: () => void;
  loadMore: () => void;
}

const useCollectorScroll = ({
  searchTerm,
  perPage = 4,
}: useCollectorScrollProps = {}): CollectionsData => {
  const [collections, setCollections] = useState<CollectionOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [refreshFlag, setRefreshFlag] = useState<boolean>(false);

  const handleRefresh = useCallback(() => {
    console.log("Refreshing collections...");
    setCollections([]);
    setPage(1);
    setHasMore(true);
    setRefreshFlag((prevFlag) => !prevFlag); // Toggle flag to trigger refetch
  }, []);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      setPage((prevPage) => prevPage + 1);
    }
  }, [loading, hasMore]);

  useEffect(() => {
    const fetchCollections = async () => {
      console.log("Fetching collections...");
      setLoading(true);
      try {
        const response = await getCollectors({ page, perPage, searchTerm });
        if (page === 1) {
          setCollections(response.data);
        } else {
          setCollections((prevCollections) => [
            ...prevCollections,
            ...response.data,
          ]);
        }
        setHasMore(response.data.length === perPage);
      } catch (error) {
        console.error("Error fetching collections:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCollections();
  }, [page, perPage, searchTerm, refreshFlag]); // Include `refreshFlag` to refetch on handleRefresh

  return { collections, loading, handleRefresh, loadMore };
};

export default useCollectorScroll;
